import { Product, ProductParameter } from 'depoto-core/src/entities'
import { Injectable } from '@angular/core'
import { CoreService } from './core.service'
import { SchemaUtil } from '../utils'
import { HttpClient } from '@angular/common/http'
import { ProductDetailService } from './product-detail.service'
import { FileUtilService } from '../utils/file-util.service'
import * as Sentry from '@sentry/angular-ivy'
import { firstValueFrom } from 'rxjs'
import { MD_CLASS_EXTERNAL_ID, ProductMdClassService } from './product-md-class.service'

@Injectable({
  providedIn: 'root',
})
export class ProductCreatingService {
  parentToCreate
  mainImageToCreate
  mainImageToCreateOnParent

  childrenIdsToPushToCreatingProduct = []
  childrenWithImagesToCreate = []

  errorsParent = []

  constructor(
    private core: CoreService,
    private http: HttpClient,
    private productDetailService: ProductDetailService,
    private fileUtilService: FileUtilService,
    private productMdClassService: ProductMdClassService,
  ) {}

  async getProductValues(template, isParent = false, vats): Promise<Product> {
    let parsedCategories = []
    if (!isParent && template.categories?.length) {
      parsedCategories = await this.getCategoriesForProduct(template)
    }

    const product = new Product({
      categories: parsedCategories,
      purchaseCurrency: this.core.services.user.user?.company?.currency,
      children: !isParent && template.children?.length > 0 ? template.children : [],
      description: template.description || '',
      code: template.mCode || '',
      code2: template.dlCode || '',
      code3: template.manuCode || '',
      ean: template.ean || '',
      ean2: template.ean2 || '',
      ean3: template.ean3 || '',
      ean4: template.ean4 || '',
      ean5: template.ean5 || '',
      purchasePrice: template.purchasePrice || '',
      externalId: template.hsId || '',
      tags:
        template.packQuantity > 1
          ? this.productDetailService.tags.filter(tag => tag.externalId === 'hs-count-by-piece')
          : [],
      manuCode: template.manuCode || '',
      name: template.name || '',
      enabled: true,
      vat: template.vatRate !== undefined ? vats.find(vat => vat.percent === template.vatRate) : { id: 1 },
      parent: !isParent && template.parent ? template.parent : null,
      ...(this.core.services.user.user?.company?.batchExpirationMode === 'enabled-everywhere' && {
        enabledBatchExpiration: true,
      }),
      customData: { vatHs: template.vatRate },
    })

    const mdClass = this.productMdClassService.normalizeMdClass(template?.mdClass)
    if (mdClass) {
      product.productParameters = [
        {
          value: mdClass,
          externalId: MD_CLASS_EXTERNAL_ID,
        } as ProductParameter,
      ]
    }

    return product
  }

  async getCategoriesForProduct(template) {
    const categories = []
    for (const category of template.categories) {
      const categoryResult = await this.core.services.category.getList(
        {
          filters: {
            externalId: category.id,
          },
        },
        SchemaUtil.category,
      )
      if (categoryResult?.items[0]?.id) categories.push(categoryResult?.items[0])
    }
    return categories
  }

  async checkIfProductAlreadyExists(hsId) {
    const fetchedProductInArray = await this.core.services.product.getList(
      {
        filters: {
          externalId: hsId,
        },
      },
      SchemaUtil.product,
    )
    return fetchedProductInArray.items[0]?.id
  }

  async createParentIfNotExists(parent, vats, suppliers?): Promise<Product> {
    const fetchedParentInArray = await this.core.services.product.getList(
      {
        filters: {
          fulltext: parent.mCode,
        },
      },
      SchemaUtil.product,
    )
    if (fetchedParentInArray?.items?.length < 1) {
      // check parent image (save if exists)
      if (parent.images?.length > 0 || parent.mainImage?.url) {
        this.mainImageToCreateOnParent = parent.mainImage?.url ? parent.mainImage : parent.images[0]
      }
      // create parent
      const parentToCreate = await this.getProductValues(parent, true, vats)
      parentToCreate.supplier = suppliers.find(supplier => supplier.externalId === 'henryschein')
      let result
      try {
        result = await this.core.services.product.create(parentToCreate, SchemaUtil.product)
      } catch (err) {
        console.log(err)
        this.errorsParent.push(parent.hsId)
      }
      if (result?.id) {
        const parentMdClass =
          this.productMdClassService.getCurrentValue(parentToCreate) ||
          this.productMdClassService.normalizeMdClass(parent?.mdClass)
        if (parentMdClass) {
          await this.productMdClassService.save(result, parentMdClass)
        }
      }
      // create image
      if (result && this.mainImageToCreateOnParent) {
        await this.createImage(this.mainImageToCreateOnParent, result.id)
        this.mainImageToCreateOnParent = null
      }
      return result
    } else if (fetchedParentInArray.items.some(p => p.code === parent.mCode)) {
      return fetchedParentInArray.items.find(p => p.code === parent.mCode)
    }
  }

  async createOrUpdateProductPack(product: Product) {
    if (!product?.id) {
      console.log('productCreatingService:createOrUpdateProductMovePack: undefined product id, ', { product })
      return
    }
    const result = await this.core.services.productPack.getList(
      { filters: { product: product.id } },
      SchemaUtil.productPack,
    )
    if (result?.items?.length > 0) {
      const pack = result.items[0]
      pack.quantity = this.productDetailService.piecesInPack || 1
      await this.core.services.productPack.update(pack)
    } else {
      const pack = {
        id: null,
        product: new Product({
          id: product.id,
          ...(this.core.services.user.user?.company?.batchExpirationMode === 'enabled-everywhere' && {
            enabledBatchExpiration: true,
          }),
        }),
        quantity: this.productDetailService.piecesInPack || 1,
        externalId: 'hs-count-by-piece',
        ean: product.ean,
        code: product.code,
        note: undefined,
        weight: undefined,
      }
      await this.core.services.productPack.create(pack, SchemaUtil.productPack)
    }
  }

  async checkIfChildrenExist(children, vats, suppliers?) {
    this.childrenIdsToPushToCreatingProduct = []
    for (const child of children) {
      const fetchedChild = await this.fetchChild(child)

      if (fetchedChild?.id) {
        this.childrenIdsToPushToCreatingProduct.push(fetchedChild)
      } else {
        let imageToCreate = null
        if (child.mainImage?.url?.length > 0 || child.images?.length > 0) {
          imageToCreate = child.mainImage?.url?.length > 0 ? child.mainImage : child.images[0]
        }
        const childToCreate = await this.getProductValues(child, true, vats)
        childToCreate.supplier = suppliers.find(supplier => supplier.externalId === 'henryschein')
        if (childToCreate?.code) {
          this.childrenWithImagesToCreate.push({ childToCreate, imageToCreate })
        }
      }
    }
    return this.childrenIdsToPushToCreatingProduct
  }

  async fetchChild(child) {
    let fetchedChildren
    try {
      fetchedChildren = await this.core.services.product.getList(
        {
          filters: {
            fulltext: child.mCode,
          },
        },
        SchemaUtil.product,
      )
    } catch (err) {
      console.log(err)
      return
    }
    return fetchedChildren?.items[0]
  }

  async createImage(image, id) {
    try {
      if (typeof image === 'string') {
        image = { url: image }
      }
      if (!image?.url || !id) {
        return
      }
      image.mimeType = this.getImageMimeType(image)
      image.type = image.mimeType
      image.name = this.getImageFilename(image)
      await this.downloadImage(image, id)
      await this.reloadProductImages(id)
    } catch (err) {
      console.log(err)
    }
  }

  getFirstImage(template) {
    const images = template?.images
    if (!images) {
      return null
    }
    return Array.isArray(images) ? images[0] || null : images
  }

  clearOptions() {
    this.parentToCreate = null
    this.mainImageToCreateOnParent = null
    this.childrenIdsToPushToCreatingProduct = []
    this.childrenWithImagesToCreate = []
    this.errorsParent = []
  }

  clearImageToCreate() {
    this.mainImageToCreate = null
  }

  async downloadImage(file, id) {
    const blob = await this.fetchImageBlob(file.url)
    const prepared = {
      ...file,
      name: this.getImageFilename(file, blob),
      mimeType: this.getImageMimeType(file, blob),
    }
    const processedFile = await this.fileUtilService.processDownloadedData(blob, prepared)
    processedFile.mimeType = processedFile.mimeType || prepared.mimeType || 'image/jpeg'
    processedFile.originalFilename = processedFile.originalFilename || prepared.name
    processedFile.text = processedFile.text || prepared.name
    const created = await this.core.services.file.create(processedFile, id)
    if (!created) {
      throw new Error('createFile failed')
    }
  }

  private async fetchImageBlob(url: string): Promise<Blob> {
    try {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' })
      if (!response.ok) {
        throw new Error(`Image download failed: ${response.status}`)
      }
      return await response.blob()
    } catch (e) {
      return await firstValueFrom(this.http.get(url, { responseType: 'blob' }))
    }
  }

  private getImageFilename(file, blob?: Blob): string {
    if (file?.name) {
      return file.name
    }
    if (file?.originalFilename) {
      return file.originalFilename
    }
    const path = (file?.url || '').split('?')[0]
    const last = path.split('/').pop()
    if (last && /\.[a-z0-9]+$/i.test(last)) {
      return last
    }
    const mime = this.getImageMimeType(file, blob)
    const ext = (mime.split('/')[1] || 'jpg').replace('jpeg', 'jpg')
    return `image.${ext}`
  }

  private getImageMimeType(file, blob?: Blob): string {
    if (file?.mimeType) {
      return file.mimeType
    }
    if (file?.type) {
      return file.type
    }
    if (blob?.type && blob.type !== 'application/octet-stream') {
      return blob.type
    }
    const path = (file?.url || '').split('?')[0].toLowerCase()
    if (path.endsWith('.png')) {
      return 'image/png'
    }
    if (path.endsWith('.gif')) {
      return 'image/gif'
    }
    if (path.endsWith('.webp')) {
      return 'image/webp'
    }
    return 'image/jpeg'
  }

  private async reloadProductImages(id: number) {
    if (this.productDetailService.product?.id !== id) {
      return
    }
    const product = await this.core.services.product.getById(id, SchemaUtil.productFilesAndImages)
    if (!product) {
      return
    }
    this.productDetailService.product.images = [...(product.images || [])]
    this.productDetailService.product.mainImage = product.mainImage
  }
}
